import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, BarChart3, Check, Code2, Phone, Sparkles, Copy, ExternalLink, Globe, KeyRound, MessagesSquare, RefreshCw, Settings2, Trash2, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { TabbedPage } from '@/components/TabbedPage';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MediaUpload } from '@/components/MediaUpload';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Switch, SettingsSaveBar } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { aiAgentsService, AiAgent, AiAgentWebchat, embedSnippet, frameUrl, PRE_CHAT_OPTIONS, PUBLIC_API_BASE } from '@/services/ai-agents.service';
import { COLOR_PRESETS, TabBody, WidgetPreview } from './agentShared';
import { OverviewTab, PersonaTab, WhatsappTab } from './agentTabs';

type WebchatDraft = Omit<AiAgentWebchat, 'widgetKey'>;

export default function AiAgentDetail() {
  const { agentUid = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [agent, setAgent] = useState<AiAgent | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    aiAgentsService.get(agentUid).then(setAgent).catch(() => setMissing(true));
  }, [agentUid]);

  if (missing) {
    return (
      <>
        <Topbar title="Agent not found" breadcrumb="AI Agents / Agents" />
        <div className="px-7 py-10 text-sm text-text-muted">This agent was deleted or never existed. <Link className="text-primary underline" to="/ai-agents">Back to agents</Link></div>
      </>
    );
  }
  if (!agent) return <><Topbar title="Loading…" breadcrumb="AI Agents / Agents" /><div className="px-7 py-7"><div className="skeleton h-96 rounded-2xl" /></div></>;

  return (
    <TabbedPage
      title={agent.name}
      breadcrumb={`AI Agents / ${agent.name}`}
      right={<Button variant="secondary" size="sm" iconLeft={<MessagesSquare size={14} />} onClick={() => navigate(`/ai-agents/conversations?agent=${agent.agentUid}`)}>Conversations</Button>}
      tabs={[
        { id: 'overview', label: 'Overview', icon: <BarChart3 size={14} />, render: () => <TabBody><OverviewTab agent={agent} /></TabBody> },
        { id: 'persona', label: 'Persona & knowledge', icon: <Sparkles size={14} />, render: () => <TabBody><PersonaTab agent={agent} isAdmin={isAdmin} onSaved={setAgent} /></TabBody> },
        { id: 'website', label: 'Website chat', icon: <Globe size={14} />, render: () => <TabBody><WebsiteTab agent={agent} isAdmin={isAdmin} onSaved={setAgent} /></TabBody> },
        { id: 'api', label: 'Chat API', icon: <Code2 size={14} />, render: () => <TabBody><ApiTab agent={agent} isAdmin={isAdmin} onSaved={setAgent} /></TabBody> },
        { id: 'whatsapp', label: 'WhatsApp', icon: <Phone size={14} />, render: () => <TabBody><WhatsappTab agent={agent} isAdmin={isAdmin} /></TabBody> },
        { id: 'settings', label: 'Settings', icon: <Settings2 size={14} />, render: () => <TabBody><SettingsTab agent={agent} isAdmin={isAdmin} onSaved={setAgent} onDeleted={() => navigate('/ai-agents')} /></TabBody> },
      ]}
    />
  );
}

function draftOf(agent: AiAgent): WebchatDraft {
  const { widgetKey: _key, ...rest } = agent.webchat;
  return { ...rest, title: rest.title ?? '', greeting: rest.greeting ?? '', logoUrl: rest.logoUrl ?? '', allowedDomains: rest.allowedDomains ?? [] };
}

// ─── Website chat ─────────────────────────────────────────────────────────

function WebsiteTab({ agent, isAdmin, onSaved }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void }) {
  const [draft, setDraft] = useState<WebchatDraft>(() => draftOf(agent));
  const [saving, setSaving] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const saved = useMemo(() => draftOf(agent), [agent]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (patch: Partial<WebchatDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const live = agent.isActive && agent.webchat.enabled;

  const save = async (patch?: Partial<WebchatDraft>) => {
    const body = { ...draft, ...patch };
    setSaving(true);
    try {
      const updated = await aiAgentsService.update(agent.agentUid, { webchat: body });
      onSaved(updated);
      setDraft(draftOf(updated));
      toast.success('Website chat saved');
    } catch (e: any) {
      if (!e?.response) toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenOpen(false);
    try {
      onSaved(await aiAgentsService.regenerateKey(agent.agentUid));
      toast.success('New key issued — update the snippet on your site');
    } catch (e: any) {
      if (!e?.response) toast.error('Could not issue a new key');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-5">
        <Card>
          <div className="flex flex-wrap items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                Show the chat on your website
                <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', live ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted')}>{live ? 'Live' : 'Off'}</span>
              </div>
              <p className="mt-1 text-xs text-text-muted">
                {agent.isActive ? 'Off hides the chat everywhere the snippet is installed, instantly.' : 'This agent is switched off in Settings, so the chat stays hidden.'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {live && (
                <a href={frameUrl(agent.webchat.widgetKey)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  Try it <ExternalLink size={12} />
                </a>
              )}
              <Switch checked={draft.enabled} disabled={!isAdmin || saving} label="Show the chat on your website" onChange={(enabled) => { set({ enabled }); void save({ enabled }); }} />
            </div>
          </div>
        </Card>

        <Card>
          <SectionHeader title="Install on your site" icon={<Code2 size={16} />} className="mb-3" subtitle="Paste this once, just before </body>, on every page that should show the chat." />
          <CodeBlock code={embedSnippet(agent.webchat.widgetKey)} />
          <ul className="mt-3 space-y-1 text-xs text-text-muted">
            <li><b className="text-text-secondary">WordPress:</b> a header &amp; footer plugin (e.g. WPCode) → Footer. <b className="text-text-secondary">Shopify:</b> Online Store → Themes → Edit code → theme.liquid.</li>
            <li>Add <code className="rounded bg-surface-alt px-1">&amp;side=left</code> to the URL to override the side on one site.</li>
          </ul>
          {isAdmin && (
            <button type="button" onClick={() => setRegenOpen(true)} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-text-muted hover:text-danger">
              <RefreshCw size={12} /> Issue a new key (the old snippet stops working)
            </button>
          )}
        </Card>

        <Card>
          <SectionHeader title="Where it can appear" icon={<Globe size={16} />} className="mb-3" subtitle="Browsers refuse to show the chat on any other site. A domain also covers its subdomains." />
          <DomainsInput value={draft.allowedDomains} onChange={(allowedDomains) => set({ allowedDomains })} disabled={!isAdmin} />
          {draft.allowedDomains.length === 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-warning"><AlertTriangle size={13} className="mt-px shrink-0" /> Any website can show this chat right now. Add your domain so a copied snippet can't run your AI on someone else's site.</p>
          )}
        </Card>

        <Card>
          <SectionHeader title="Look & first message" icon={<MessagesSquare size={16} />} className="mb-3" />
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <FormField label="Chat title" hint="Shown in the header">
              <Input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder={agent.name} maxLength={60} disabled={!isAdmin} />
            </FormField>
            <FormField label="Side">
              <div className="inline-flex rounded-xl border border-border bg-surface-alt p-1">
                {(['right', 'left'] as const).map((p) => (
                  <button key={p} type="button" disabled={!isAdmin} onClick={() => set({ position: p })}
                    className={clsx('rounded-lg px-4 py-1.5 text-xs font-medium capitalize transition-colors', draft.position === p ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>
                    {p}
                  </button>
                ))}
              </div>
            </FormField>
          </div>
          <FormField label="Greeting" hint="Empty = the WhatsApp AI greeting" hintBelow>
            <Textarea rows={2} value={draft.greeting} onChange={(e) => set({ greeting: e.target.value })} maxLength={500} disabled={!isAdmin} placeholder="Hi! How can we help you today?" />
          </FormField>
          <FormField label="Brand colour">
            <div className="flex flex-wrap items-center gap-2">
              {COLOR_PRESETS.map((c) => (
                <button key={c} type="button" disabled={!isAdmin} onClick={() => set({ color: c })} aria-label={`Colour ${c}`}
                  className={clsx('flex h-8 w-8 items-center justify-center rounded-full border-2 transition-transform hover:scale-110', draft.color.toLowerCase() === c.toLowerCase() ? 'border-text-primary' : 'border-transparent')}
                  style={{ background: c }}>
                  {draft.color.toLowerCase() === c.toLowerCase() && <Check size={14} className="text-white" />}
                </button>
              ))}
              <div className="w-28"><Input value={draft.color} onChange={(e) => set({ color: e.target.value })} maxLength={7} disabled={!isAdmin} aria-label="Hex colour" /></div>
            </div>
          </FormField>
          <FormField label="Logo" hint="Square image, shown in the chat header" hintBelow>
            <MediaUpload mode="image" compact folder="ai_agents/logos" value={draft.logoUrl} onChange={(logoUrl) => set({ logoUrl })} />
          </FormField>
        </Card>

        <Card>
          <SectionHeader title="Before the chat" icon={<KeyRound size={16} />} className="mb-3" subtitle="What visitors fill in first. Chats with a phone or email become leads in your CRM." />
          <SearchableSelect
            options={PRE_CHAT_OPTIONS.map((o) => ({ value: o.value, label: o.label, description: o.description }))}
            value={draft.preChat}
            onChange={(v) => set({ preChat: v as WebchatDraft['preChat'] })}
            disabled={!isAdmin}
            numbered={false}
            searchable={false}
            aria-label="Before the chat"
          />
        </Card>

        {dirty && isAdmin && <SettingsSaveBar onDiscard={() => setDraft(saved)} onSave={() => void save()} saving={saving} disabled={!/^#[0-9a-fA-F]{6}$/.test(draft.color)} note={!/^#[0-9a-fA-F]{6}$/.test(draft.color) ? <span className="text-xs text-danger">Colour must look like #B8913A</span> : undefined} />}
      </div>

      <div className="min-w-0 lg:sticky lg:top-4 lg:self-start">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Preview</div>
        <WidgetPreview cfg={{ ...draft, color: /^#[0-9a-fA-F]{6}$/.test(draft.color) ? draft.color : agent.webchat.color }} title={draft.title || agent.name} />
        <p className="mt-2 text-xs text-text-muted">Follows your unsaved changes. Replies come from the agent’s AI once live.</p>
      </div>

      <ConfirmDialog
        open={regenOpen}
        title="Issue a new key?"
        message="Every page still using the current snippet stops showing the chat until you paste the new one. Use this if the snippet was copied somewhere it shouldn't be."
        danger
        onConfirm={regenerate}
        onCancel={() => setRegenOpen(false)}
      />
    </div>
  );
}

function DomainsInput({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  const [text, setText] = useState('');
  const add = () => {
    const parts = text.split(/[\s,]+/).map((p) => p.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^\*\./, '')).filter(Boolean);
    if (!parts.length) return;
    const bad = parts.find((p) => !/^[a-z0-9.-]+(:\d+)?$/.test(p) || p.startsWith('.') || (!p.includes('.') && !p.startsWith('localhost')));
    if (bad) { toast.error(`"${bad}" doesn't look like a domain`); return; }
    onChange(Array.from(new Set([...value, ...parts])));
    setText('');
  };
  return (
    <div>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="yourshop.com" disabled={disabled} aria-label="Add a domain" />
        </div>
        <Button variant="secondary" onClick={add} disabled={disabled || !text.trim()}>Add</Button>
      </div>
      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-alt py-1 pl-3 pr-1.5 text-xs font-medium text-text-primary">
              {d}
              {!disabled && <button type="button" onClick={() => onChange(value.filter((x) => x !== d))} aria-label={`Remove ${d}`} className="rounded-full p-0.5 text-text-muted hover:bg-danger-soft hover:text-danger"><X size={12} /></button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Copy failed — select the text instead');
    }
  };
  return (
    <div className="relative rounded-xl border border-border bg-surface-alt">
      <pre className="overflow-x-auto whitespace-pre-wrap break-all p-3 pr-20 font-mono text-[12px] leading-relaxed text-text-primary">{code}</pre>
      <button type="button" onClick={copy} className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary hover:text-text-primary">
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

// ─── Chat API ─────────────────────────────────────────────────────────────

function ApiTab({ agent, isAdmin, onSaved }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void }) {
  const [saving, setSaving] = useState(false);
  const base = `${PUBLIC_API_BASE}/public/v1/agent`;
  const toggle = async (apiEnabled: boolean) => {
    setSaving(true);
    try { onSaved(await aiAgentsService.update(agent.agentUid, { apiEnabled })); } catch (e: any) { if (!e?.response) toast.error('Could not save'); } finally { setSaving(false); }
  };
  const start = `curl -X POST ${base}/conversations \\
  -H "Authorization: Bearer $SLIGHTOR_KEY" -H "Content-Type: application/json" \\
  -d '{"agentId":"${agent.agentUid}","externalUserId":"user-42","name":"Ali","phone":"+971501234567"}'`;
  const send = `curl -X POST ${base}/conversations/<conversationId>/messages \\
  -H "Authorization: Bearer $SLIGHTOR_KEY" -H "Content-Type: application/json" \\
  -d '{"text":"Do you deliver to Dubai Marina?"}'`;
  const poll = `curl "${base}/conversations/<conversationId>/messages?after=<createdAt of the last message you have>" \\
  -H "Authorization: Bearer $SLIGHTOR_KEY"`;

  return (
    <div className="max-w-4xl space-y-5">
      <Card>
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-text-primary">Answer through the chat API</div>
            <p className="mt-1 text-xs text-text-muted">Put this agent inside your own Android, iOS or web app. Replies come back in the same request; team replies arrive when you poll.</p>
          </div>
          <Switch checked={agent.apiEnabled} disabled={!isAdmin || saving} label="Answer through the chat API" onChange={toggle} />
        </div>
      </Card>
      <Card>
        <SectionHeader title="1 · Create a key" icon={<KeyRound size={16} />} className="mb-2" />
        <p className="text-sm text-text-secondary">
          Settings → <Link to="/settings/developer" className="font-medium text-primary hover:underline">Developer API</Link> → New key → tick <code className="rounded bg-surface-alt px-1 text-xs">agent:chat</code>.
        </p>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-warning"><AlertTriangle size={13} className="mt-px shrink-0" /> Keep the key on your server. An app should call your backend, which calls Slightor — a key shipped inside an app can be extracted.</p>
      </Card>
      <Card>
        <SectionHeader title="2 · Start a conversation" className="mb-2" subtitle="Send your own user id as externalUserId — sending it again returns that user's open conversation." />
        <CodeBlock code={start} />
        <div className="mt-2 text-xs text-text-muted">Agent id: <code className="rounded bg-surface-alt px-1">{agent.agentUid}</code></div>
      </Card>
      <Card>
        <SectionHeader title="3 · Send messages" className="mb-2" subtitle="Returns the customer message and the AI reply. aiPaused: true means your team has the chat — show their replies by polling." />
        <CodeBlock code={send} />
      </Card>
      <Card>
        <SectionHeader title="4 · Poll for team replies" className="mb-2" subtitle="Every few seconds while the chat screen is open." />
        <CodeBlock code={poll} />
        <p className="mt-3 text-xs text-text-muted">Limits: 120 messages a minute per key. A message sent while the previous reply is still being written answers 409 — retry after a moment.</p>
      </Card>
    </div>
  );
}

// ─── Settings ─────────────────────────────────────────────────────────────

function SettingsTab({ agent, isAdmin, onSaved, onDeleted }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void; onDeleted: () => void }) {
  const [name, setName] = useState(agent.name);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dirty = name.trim() !== agent.name;

  const save = async (patch: Parameters<typeof aiAgentsService.update>[1]) => {
    setSaving(true);
    try {
      onSaved(await aiAgentsService.update(agent.agentUid, patch));
      toast.success('Saved');
    } catch (e: any) {
      if (!e?.response) toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setDeleting(false);
    try { await aiAgentsService.remove(agent.agentUid); toast.success('Agent deleted'); onDeleted(); } catch (e: any) { if (!e?.response) toast.error('Could not delete'); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Card>
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-text-primary">Agent is on</div>
            <p className="mt-1 text-xs text-text-muted">Off stops the AI on every channel. Visitors can still message; your team answers from Conversations.</p>
          </div>
          <Switch checked={agent.isActive} disabled={!isAdmin || saving} label="Agent is on" onChange={(isActive) => save({ isActive })} />
        </div>
      </Card>
      <Card>
        <SectionHeader title="Name" className="mb-3" subtitle="Your team sees it in the CRM; visitors see the chat title (Website chat tab)." />
        <div className="flex gap-2">
          <div className="min-w-0 flex-1"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} disabled={!isAdmin} aria-label="Agent name" /></div>
          {isAdmin && <Button disabled={!dirty || !name.trim()} loading={saving} onClick={() => save({ name: name.trim() })}>Save</Button>}
        </div>
      </Card>
      {isAdmin && (
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-text-primary">Delete agent</div>
              <p className="mt-1 text-xs text-text-muted">The website chat and API stop at once. Past conversations and leads stay.</p>
            </div>
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={14} />} onClick={() => setDeleting(true)}>Delete</Button>
          </div>
        </Card>
      )}
      <ConfirmDialog
        open={deleting}
        title={`Delete "${agent.name}"?`}
        message="The chat disappears from every site using its snippet and API calls start failing. Conversations and leads are kept."
        danger
        onConfirm={remove}
        onCancel={() => setDeleting(false)}
      />
    </div>
  );
}
